import type { ChangeEventHandler } from "react"
import { useCallback, useMemo, useRef, useState } from "react"

import { useSelector } from "@ggs/store"
import { type iconKeys, SvgIcon } from ".."
import { type FORM_VALUES } from "./_data"
import {
  AutocompletePopover,
  Input,
  InputClearBtn,
  InputContainer,
  InputControl,
  InputIcon,
  InputLabel,
  ListItem,
  ListTitle,
} from "./_styles"

import { HeroSearchFormProps } from "./HeroSearchForm"

import { offset, shift, size } from "@floating-ui/react"
import { Unstable_Popup as BasePopup } from "@mui/base/Unstable_Popup"
import { useMediaQuery } from "@mui/material"
import { styled, useTheme } from "@mui/material/styles"
import match from "autosuggest-highlight/match"
import parse from "autosuggest-highlight/parse"
import { debounce, get } from "lodash"
import { useFocusWithin } from "react-aria"
import { useController, useFormContext } from "react-hook-form"

const DESTINATION_ICONS: Record<string, iconKeys> = {
  "Africa": "destinationAfrica",
  "Arctic & Antarctica": "destinationArctic",
  "Asia": "destinationAsia",
  "Australia & New Zealand": "destinationAustralia",
  "Central America": "destinationCentralAmerica",
  "Europe": "destinationEurope",
  "Indian Ocean": "destinationIndianOcean",
  "Middle East": "destinationMiddleEast",
  "South America": "destinationSouthAmerica",
  "South Pacific": "destinationSouthPacific",
  "US & Canada": "destinationUSCanada",
}

type Option = {
  label: string
  value: string
  type: string // destination | country | location
  disabled?: boolean
}

const LocationInput = ({ variant }: HeroSearchFormProps) => {
  // Get the initial values and options from the store
  const tripSearch = useSelector((state) => state.views.index.tripSearch)
  const defaultDestinations: Option[] = get(tripSearch, "defaultValues.destinations", [])
  const defaultLocations: Option[] = get(tripSearch, "defaultValues.locations", [])
  const destinationOptions: Option[] = get(tripSearch, "options.destinations", [])
  const locationOptions: Option[] = get(tripSearch, "options.locations", [])

  // Form context hooks
  const { setFocus, setValue, resetField } = useFormContext<FORM_VALUES>()
  const {
    field: { name, value, onChange, onBlur, ref },
    fieldState: { isDirty },
  } = useController({
    name: "location",
  })
  // Use state to manage the input value for debouncing
  const [inputValue, setInputValue] = useState(value)

  // Memoize the rendered destination options
  const renderedDestinationOptions = useMemo(() => {
    // Go through the default destinations and check if they are in the destination options
    // If they are not, disable them
    return defaultDestinations.map((destination) => {
      const option = destinationOptions.find((x) => x.value === destination.value)
      return {
        ...destination,
        value: `${destination.label}:${destination.value}`,
        type: "destination",
        disabled: !option,
      }
    })
  }, [defaultDestinations, destinationOptions, inputValue])

  // Memoize the rendered autocomplete options
  const renderedAutocompleteOptions = useMemo(() => {
    if (!inputValue || inputValue === "") return defaultLocations.slice(0, 5)
    // Go through the default locations and check if they are in the location options
    // If they are not, disable them
    // Also, filter the options based on the input value and limit to 5 results
    const options = defaultLocations
      .map((location) => {
        const option = locationOptions.find((x) => x.value === location.value)
        return { ...location, value: `${location.label}:${location.value}`, disabled: !option }
      })
      .filter((option) => option.label.toLowerCase().includes(inputValue.toLowerCase()))
      .slice(0, 5)

    // If no results are found, return an error message
    if (options.length === 0)
      return [{ label: "No results found", value: "", type: "error", disabled: true }]

    return options
  }, [defaultLocations, locationOptions, inputValue])

  // Popper hooks
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [popupOpen, setPopupOpen] = useState<"destination" | "autocomplete" | null>(null)
  const [isFocusWithin, setFocusWithin] = useState(false)
  const [hasBlurred, setHasBlurred] = useState(false)
  const { focusWithinProps } = useFocusWithin({
    onFocusWithin: () => {
      setPopupOpen("autocomplete")
    },
    onBlurWithin: () => {
      setPopupOpen(null)
      if (!renderedAutocompleteOptions.some((x) => x.label === inputValue)) {
        resetField(name)
        setInputValue("")
        setValue("destination", "")
        setValue("country", "")
      }
      if (inputValue !== "") setHasBlurred(true)
    },
    onFocusWithinChange: (isFocusWithin) => setFocusWithin(isFocusWithin),
  })
  const destinationPopupId = popupOpen === "destination" ? "destination-popover" : undefined
  const autocompletePopupId = popupOpen === "autocomplete" ? "autocomplete-popover" : undefined
  const [autocompleteFocusIndex, setAutocompleteFocusIndex] = useState<number | null>(null)
  const autocompleteListRef = useRef<Array<HTMLDivElement | null>>([])
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"))

  // Handle the change event on the input
  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    onChange(event)
    setFocus(name)
    // If the input value is empty, clear the destination and country fields
    if (event.target.value === "") {
      setValue("destination", "")
      setValue("country", "")
      setPopupOpen("autocomplete")
    } else {
      // If the input value is not empty, open the autocomplete popover
      setPopupOpen("autocomplete")
      const option = renderedAutocompleteOptions.find(
        (x) => x.label.toLowerCase() === event.target.value.toLowerCase()
      )

      // If the option is not found, clear the destination and country fields
      if (!option) {
        setValue("destination", "")
        setValue("country", "")
        return
      }

      if (option.disabled) return
      // If the option is found, set the value based on the type and clear the other field
      else if (option.type === "destination" || option.type === "country") {
        setInputValue(option.label)
        setValue(option.type, option.value)
        setValue(option.type === "destination" ? "country" : "destination", "")
      }
    }
  }

  // Debounce the change event
  const handleDebouncedChange = useCallback(
    debounce((handleChange, e) => handleChange(e), 250),
    []
  )

  // Handle the clear button click event
  const handleClear = () => {
    resetField(name)
    setValue("destination", "")
    setValue("country", "")
    setInputValue("")
    setPopupOpen(null)
    onBlur()
    setFocusWithin(false)
  }

  // Handle the click event on the destination list item in the popover
  const handleItemClick = (option: Option) => {
    if (option.disabled) return

    if (option.label === inputValue) {
      setInputValue("")
      resetField(name)
      setValue("destination", "")
      setValue("country", "")
      setPopupOpen("destination")
    } else if (option.type === "destination" || option.type === "country") {
      setPopupOpen(null)
      onChange(option.label)
      setValue(option.type, option.value)
      setValue(option.type === "destination" ? "country" : "destination", "")
      setInputValue(option.label)
      setFocus(name)
    }
  }

  return (
    <>
      <InputContainer
        ref={containerRef}
        focused={isFocusWithin}
        {...focusWithinProps}
        tabIndex={popupOpen ? 0 : -1}
        variant={variant}
        onClick={() => {
          setFocusWithin(true)
          setFocus(name)
          if (popupOpen) return
        }}
      >
        <InputIcon>
          <SvgIcon variant="pinMap2" size={24} />
        </InputIcon>
        <InputControl variant={variant}>
          <InputLabel htmlFor="input-location">Where</InputLabel>
          <Input
            ref={ref}
            id="input-location"
            name={name}
            placeholder={"Search country"}
            autoComplete="off"
            spellCheck={false}
            value={inputValue || ""}
            onChange={(e) => {
              setInputValue(e.target.value)
              handleDebouncedChange(handleChange, e)
            }}
            onBlur={onBlur}
            onKeyDown={(e) => {
              if (inputValue === "") return
              if (e.key === "ArrowDown") {
                e.preventDefault()
                setAutocompleteFocusIndex(0)
                autocompleteListRef.current?.[0]?.focus()
              }
              if (e.key === "ArrowUp") {
                e.preventDefault()
                setAutocompleteFocusIndex(renderedAutocompleteOptions.length - 1)
                autocompleteListRef.current?.[renderedAutocompleteOptions.length - 1]?.focus()
              }
            }}
          />
        </InputControl>
        {value !== "" && inputValue !== "" && isDirty && hasBlurred && (
          <InputClearBtn
            type="reset"
            focused={isFocusWithin}
            onClick={(e) => {
              e.stopPropagation()
              handleClear()
            }}
            onMouseDown={(e) => {
              e.preventDefault()
              e.currentTarget.blur()
            }}
          >
            <span>Clear</span>
            <SvgIcon variant="close" strokeMode size={8} />
          </InputClearBtn>
        )}
        {/* <BasePopup
          id={destinationPopupId}
          open={popupOpen === "destination"}
          anchor={containerRef.current}
          placement={isDesktop ? "top-start" : "bottom"}
          strategy="fixed"
          disablePortal
          middleware={[
            shift(),
            offset(isDesktop ? 10 : 4),
            size({
              apply({ rects, availableHeight, elements }) {
                Object.assign(elements.floating.style, {
                  height: isDesktop ? "" : `${availableHeight - 98}px`,
                  minWidth: isDesktop
                    ? `${rects.reference.width * 2}px`
                    : `${rects.reference.width}px`,
                  overflow: availableHeight >= elements.floating.scrollHeight ? "" : "auto",
                })
              },
            }),
          ]}
          slotProps={{
            root: {
              role: "listbox",
              style: {
                zIndex: 1,
              },
            },
          }}
        >
          <AutocompletePopover>
            <LocationList>
              <ListTitle sx={{ gridColumn: "1 / -1", mb: 1 }}>
                Search by Destination or Country
              </ListTitle>
              {renderedDestinationOptions.map((option) => (
                <LocationListItem
                  key={option.value}
                  tabIndex={option.disabled ? -1 : 0}
                  disabled={option.disabled ?? false}
                  role="option"
                  aria-selected={option.label === value}
                  selected={option.label === value}
                  onClick={() => handleItemClick(option as Option)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      handleItemClick(option as Option)
                    }
                  }}
                >
                  <div className="list-item__icon">
                    <SvgIcon
                      className="list-item__icon--destination"
                      variant={DESTINATION_ICONS[option.label] ?? "globeFilled"}
                      size={24}
                    />
                  </div>
                  <div className="list-item__label">{option.label}</div>
                </LocationListItem>
              ))}
            </LocationList>
          </AutocompletePopover>
        </BasePopup> */}
        <BasePopup
          id={autocompletePopupId}
          open={popupOpen === "autocomplete"}
          anchor={containerRef.current}
          placement={isDesktop && variant === "default" ? "top" : "bottom"}
          strategy="fixed"
          disablePortal
          middleware={[
            shift(),
            offset(isDesktop ? 10 : 4),
            size({
              apply({ rects, availableHeight, elements }) {
                Object.assign(elements.floating.style, {
                  minWidth: `${rects.reference.width}px`,
                  height: isDesktop ? "" : `${availableHeight - 98}px`,
                  overflow: availableHeight >= elements.floating.clientHeight ? "" : "auto",
                })
              },
            }),
          ]}
          slotProps={{
            root: {
              role: "listbox",
              style: {
                zIndex: 1,
              },
            },
          }}
        >
          <AutocompletePopover sx={{ pt: 3 }}>
            {renderedAutocompleteOptions.map((option, index) => {
              const matches = match(option.label, value || "", { insideWords: true })
              const parts = parse(option.label, matches)
              return (
                <LocationListItem
                  key={option.value}
                  ref={(node) => (autocompleteListRef.current[index] = node)}
                  tabIndex={option.disabled ? -1 : autocompleteFocusIndex === index ? 0 : -1}
                  disabled={option.disabled ?? false}
                  role="option"
                  aria-selected={option.label === value}
                  selected={option.label === inputValue}
                  onClick={(e) => {
                    e.preventDefault()
                    handleItemClick(option)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      handleItemClick(option)
                    } else if (e.key === "ArrowDown") {
                      e.preventDefault()
                      const nextIndex = Math.min(index + 1, renderedAutocompleteOptions.length)
                      if (nextIndex < renderedAutocompleteOptions.length) {
                        setAutocompleteFocusIndex(nextIndex)
                        autocompleteListRef.current?.[nextIndex]?.focus()
                      } else {
                        setAutocompleteFocusIndex(null)
                        setFocus(name)
                      }
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault()
                      const nextIndex = Math.max(index - 1, -1)
                      if (nextIndex >= 0) {
                        setAutocompleteFocusIndex(nextIndex)
                        autocompleteListRef.current?.[nextIndex]?.focus()
                      } else {
                        setAutocompleteFocusIndex(null)
                        setFocus(name)
                      }
                    }
                  }}
                >
                  <div className="list-item__icon">
                    <SvgIcon
                      className={`list-item__icon--${option.type}`}
                      variant={
                        option.type === "destination"
                          ? DESTINATION_ICONS[option.label] ?? "globeFilled"
                          : option.type === "country"
                          ? "location2"
                          : "map"
                      }
                      size={
                        option.type === "destination" ? 24 : option.type === "country" ? 17 : 20
                      }
                    />
                  </div>
                  <div className="list-item__label">
                    {parts.map((part, index) => (
                      <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                        {part.text}
                      </span>
                    ))}
                  </div>
                </LocationListItem>
              )
            })}
          </AutocompletePopover>
        </BasePopup>
      </InputContainer>
    </>
  )
}

export default LocationInput

const LocationList = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr",
  gridGap: theme.spacing(3),

  [theme.breakpoints.up("md")]: {
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },

  [theme.breakpoints.up("lg")]: {
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  },
}))

const LocationListItem = styled(ListItem)(({ theme }) => ({
  ".list-item__icon": {
    alignItems: "center",
    display: "flex",
    flexShrink: 0,
    justifyContent: "center",
    height: 20,
    width: 20,
  },
}))
